import React from 'react';
import {
  Box,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ArchiveFormProgressResult } from './archive-form.types';
import './archive-form.scss';

/** 建档完成度圆环直径，单位为像素。 */
const PROGRESS_RING_SIZE = 30;

/** 建档底部进度条属性。 */
export interface ArchiveSaveProgressBarProps {
  /** 当前建档表单的完成与阻断状态。 */
  progress: ArchiveFormProgressResult;
  /** 保存及其他关联操作按钮。 */
  actions: React.ReactNode;
  /** 是否禁用底部操作区。 */
  disabled?: boolean;
  /** 是否使用固定底栏修饰类。 */
  fixed?: boolean;
  /** 底栏位置与尺寸等内联样式。 */
  style?: React.CSSProperties;
}

/**
 * 展示建档完成度、保存阻断原因和操作按钮。
 * @param props - 完成状态、操作按钮与禁用状态
 */
export function ArchiveSaveProgressBar({
  progress,
  actions,
  disabled = false,
  fixed = false,
  style,
}: ArchiveSaveProgressBarProps) {
  // 国际化函数用于生成带数量的进度与阻断文案
  const { t } = useTranslation();
  /** 扫描核对模式下用已核对比例代替必填完成度。 */
  const reviewTotal = progress.reviewTotalCount ?? 0;
  const reviewMode = reviewTotal > 0;
  /** 圆环百分比：扫描时为核对进度，否则为必填完成度。 */
  const ringValue = reviewMode
    ? Math.round(((progress.reviewConfirmedCount ?? 0) / reviewTotal) * 100)
    : progress.percentage;
  // 当前所有保存阻断原因
  const blockingReasons = [
    progress.errorCount > 0
      ? t('archive_form_required_blocked', {
        count: progress.errorCount,
        defaultValue: '尚不可保存：{{count}} 项必填未通过',
      })
      : null,
    progress.pendingConfirmCount > 0
      ? t('archive_form_pending_review', {
        count: progress.pendingConfirmCount,
        defaultValue: '{{count}} 项识别结果待核对',
      })
      : null,
  ].filter((reason): reason is string => Boolean(reason));
  // 仅在存在阻断原因时展示状态文案，条件满足时不额外占用底栏空间。
  const blockingText = blockingReasons.join('；');

  // 状态独立于完成度显示，统一放在保存按钮前。
  const statusContent = blockingText ? (
    <Typography
      className="archive-form__blocking-status archive-form__blocking-status--error"
      variant="body2"
    >
      {blockingText}
    </Typography>
  ) : null;

  return (
    <Stack
      className={[
        'archive-form__save-progress',
        fixed ? 'archive-form__save-progress--fixed' : '',
      ].filter(Boolean).join(' ')}
      direction={{ xs: 'column', md: 'row' }}
      alignItems={{ xs: 'stretch', md: 'center' }}
      spacing={2}
      aria-disabled={disabled}
      style={style}
    >
      <Stack
        className="archive-form__progress-summary"
        direction="row"
        alignItems="center"
        spacing={2}
      >
        <Box className="archive-form__progress-ring">
          <CircularProgress
            aria-hidden
            className="archive-form__progress-ring-track"
            variant="determinate"
            value={100}
            size={PROGRESS_RING_SIZE}
          />
          <CircularProgress
            aria-label={reviewMode
              ? t('archive_form_ocr_reviewed_label', { defaultValue: '识别结果核对进度' })
              : t('archive_form_required_progress_label', { defaultValue: '建档必填完成度' })}
            className="archive-form__progress-ring-value"
            variant="determinate"
            value={ringValue}
            size={PROGRESS_RING_SIZE}
          />
          <Typography className="archive-form__progress-ring-label" variant="body2">
            {reviewMode
              ? `${progress.reviewConfirmedCount ?? 0}/${reviewTotal}`
              : `${progress.percentage}%`}
          </Typography>
        </Box>
        <Stack className="archive-form__progress-details" spacing={0.5}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2">
              {reviewMode
                ? t('archive_form_ocr_reviewed_progress', {
                  confirmed: progress.reviewConfirmedCount ?? 0,
                  total: reviewTotal,
                  defaultValue: '{{confirmed}} / {{total}} 项识别结果已核对',
                })
                : t('archive_form_required_progress', { defaultValue: '必填完成度' })}
            </Typography>
            {!reviewMode ? (
              <Typography variant="body2">
                {progress.percentage}%
              </Typography>
            ) : null}
          </Stack>
        </Stack>
      </Stack>
      {statusContent}
      <Box
        component="fieldset"
        className="archive-form__save-actions-fieldset"
        disabled={disabled}
      >
        <Stack
          className="archive-form__save-actions"
          direction="row"
          justifyContent="flex-end"
          spacing={1}
        >
          {actions}
        </Stack>
      </Box>
    </Stack>
  );
}
